/**
 * @name pageDesigner
 *
 * @description
 * Contains all utilities about page designer mapping data that can be used in different components.
 */
import type {Page, LinkObject, SuggestionsColumn, ProductSuggestions} from '_types'
import {createRedirectUrl} from '_utils'

/**
 * Maps the page designer data of search section.
 *
 * @param {LinkObject} linkObject
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {Object} obj - An object containing ideas, women and men informations.
 * @returns {SuggestionsColumn} obj.ideasData - An object containing the title of the ideas column and relative suggestion items.
 * @returns {SuggestionsColumn} obj.womenData - An object containing the title of the women column and relative suggestion items.
 * @returns {SuggestionsColumn} obj.menData - An object containing the title of the men column and relative suggestion items.
 * @returns {ProductSuggestions} obj.productsData - An object containing the title of the products box and relative product items.
 */
export const mapSearchSectionPageDesignerData = (
    linkObject: LinkObject,
    pageComponents?: Page,
): {ideasData?: SuggestionsColumn; womenData?: SuggestionsColumn; menData?: SuggestionsColumn; productsData?: ProductSuggestions} => {
    const components = pageComponents?.regions?.[0]?.components

    let ideasData: SuggestionsColumn | undefined
    let womenData: SuggestionsColumn | undefined
    let menData: SuggestionsColumn | undefined
    let productsData: ProductSuggestions | undefined

    if (components) {
        const ideas = components[0]

        if (ideas?.data?.title) {
            ideasData = {
                title: {name: ideas?.data?.title, href: ideas?.data.link},
                items:
                    ideas?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const women = components[1]

        if (women?.data?.title) {
            womenData = {
                title: {name: women?.data?.title, href: women?.data.link},
                items:
                    women?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const men = components[2]

        if (men?.data?.title) {
            menData = {
                title: {name: men?.data?.title, href: men?.data.link},
                items:
                    men?.regions?.[0]?.components
                        ?.filter((component) => component.data?.title)
                        ?.map((component) => ({
                            id: component.data?.analytics_id,
                            name: component.data?.title,
                            href: createRedirectUrl(component.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                            isExternal: component.data?.target === '_blank',
                        })) ?? [],
            }
        }

        const products = components[3]

        if (products?.data?.title) {
            productsData = {
                title: products?.data?.title,
                productIds:
                    products?.regions?.[0]?.components?.filter((component) => component.data?.product)?.map((component) => component.data?.product) ?? [],
            }
        }
    }

    return {
        ideasData,
        womenData,
        menData,
        productsData,
    }
}

// Stable key fallback when component.id is missing.
export const stableKeyFromComponent = (component: any): string => {
    if (!component) return ''
    try {
        const typeId = component.typeId ?? ''
        const name = component.name ?? ''
        const data = component.data ?? {}
        const regions = component.regions ?? []
        return JSON.stringify({typeId, name, data, regions})
    } catch (_error) {
        return ''
    }
}
